import Stopwatch from '../Stopwatch'
import PhoneIcon from '@ui-components-3/icons/lib/regular/PhoneArrowUpRight'
import HangupIcon from '@ui-components-3/icons/lib/solid/CirclePhoneHangup'
import MicrophoneIcon from '@ui-components-3/icons/lib/regular/Microphone'
import MicrophoneSlashIcon from '@ui-components-3/icons/lib/regular/MicrophoneSlash'
import GearIcon from '@ui-components-3/icons/lib/regular/Gear'
import { phoneInputFormat as phoneFormatter } from 'util/formatters'
import { Observer, observer } from 'mobx-react-lite'
import { PHONE_CALL_STATUS } from 'constants/phoneCallStatuses'
import PhoneCallModel from 'models/PhoneCall'
import { ModalRef } from '@ui-components-3/ui/lib/components/Modal'
import { useCallback, useRef, useState } from 'react'
import ConfirmEndPhoneCallModal from '../ConfirmEndPhoneCallModal'
import CallDeviceSettingsModal from '../CallDeviceSettings'
import Menu from '@ui-components-3/ui/lib/components/Menu'
import HeadsetIcon from '@ui-components-3/icons/lib/regular/Headset'
import ChevronDownIcon from '@ui-components-3/icons/lib/solid/ChevronDown'
import classNames from '@ui-components-3/ui/lib/utils/classNames'

type PhoneCallProps = {
  // TODO: Improve Visit Typing
  visit: any
}

const PhoneCall = ({ visit }: PhoneCallProps) => {
  const { phoneCall }: { phoneCall: PhoneCallModel } = visit ?? {}
  const [deviceSettingsOpened, setDeviceSettingsOpened] = useState(false)
  const confirmEndPhoneCallModalRef = useRef<ModalRef>(null)

  const startPhone = () => {
    if (phoneCall?.status === PHONE_CALL_STATUS.OFFLINE) phoneCall.start()
  }

  const onConfirmEnd = () => {
    if (phoneCall?.status !== PHONE_CALL_STATUS.OFFLINE) phoneCall.end()
  }

  const toggleSettingsOpened = useCallback(
    (_open: boolean) => () => {
      setDeviceSettingsOpened(_open)
    },
    [],
  )

  const { isMuted, toggleIsMuted, startedAt } = phoneCall || {}

  const ToggleMicrophoneIcon = isMuted ? MicrophoneSlashIcon : MicrophoneIcon

  return (
    <div className="flex gap-4">
      {phoneCall?.status === PHONE_CALL_STATUS.CONNECTED && (
        <>
          <button
            type="button"
            className="btn btn-primary-outlined h-10 w-10 md:h-12 md:w-12 max-md:px-3 min-w-0 px-0 shrink-0 hover:bg-primary-100"
            onClick={() => toggleIsMuted(!isMuted)}
            aria-label={`Microphone is ${isMuted ? 'off' : 'on'}. Click here to toggle microphone`}
          >
            <ToggleMicrophoneIcon className="w-6 h-6" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-fuchsia-outlined max-w-[140px] w-full h-10 md:h-12 max-md:px-3"
            onClick={() => confirmEndPhoneCallModalRef.current.open()}
            aria-label="Hangup phone call"
          >
            <Stopwatch startedAt={startedAt} />
            <HangupIcon aria-hidden="true" className="w-6 h-6" />
          </button>
        </>
      )}
      <Menu
        className="btn-md md:btn-lg btn-primary-outlined gap-5 pl-6 shrink-0"
        aria-label="Phone call menu"
        items={[
          <button
            type="button"
            className={classNames(
              'btn btn-neutral-borderless text-neutral-800 font-medium gap-2 typography-body-l',
              !!phoneCall?.isStartPhoneCallDisabled && 'pointer-events-none',
            )}
            onClick={() => startPhone()}
            disabled={phoneCall?.isStartPhoneCallDisabled}
          >
            <PhoneIcon
              aria-hidden="true"
              className={classNames('w-6 h-6 m-1', !phoneCall?.isStartPhoneCallDisabled && 'text-primary-600')}
            />
            <span className="whitespace-pre-wrap grow">
              Call Member{'\n'}
              <Observer>{() => <>{phoneFormatter(visit.phone.number)}</>}</Observer>
            </span>
          </button>,
          <button
            type="button"
            className="btn btn-neutral-borderless text-neutral-800 font-medium gap-2 typography-body-l h-12 justify-start mt-2"
            onClick={toggleSettingsOpened(true)}
          >
            <GearIcon aria-label="Open phone call device settings modal" className="text-primary-600 w-6 h-6 m-1" />
            Device Settings
          </button>,
        ]}
        placement="bottom-end"
        itemClassName="min-w-[300px]"
        portal
        dirtyZ={100001}
      >
        <HeadsetIcon aria-hidden="true" />
        <ChevronDownIcon aria-hidden="true" />
      </Menu>
      <ConfirmEndPhoneCallModal ref={confirmEndPhoneCallModalRef} onConfirm={onConfirmEnd} />
      {deviceSettingsOpened && (
        <CallDeviceSettingsModal
          deviceSettings={phoneCall.deviceSettings}
          opened
          onClose={toggleSettingsOpened(false)}
        />
      )}
    </div>
  )
}

export default observer(PhoneCall)
